import * as React from 'react'

export interface CarouselButtonProps {
  disabled: boolean
  onClick?: () => void
}

export interface CarouselData {
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  nextButtonProps: CarouselButtonProps
  prevButtonProps: CarouselButtonProps
}

export function useCarouselDynamic(): CarouselData {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const mutationObserver = React.useRef<MutationObserver | null>(null)

  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [slidesCount, setSlidesCount] = React.useState(0)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = React.useState(false)
  const [lastSlideMostlyVisible, setLastSlideMostlyVisible] = React.useState(false)
  const updateLastSlideVisibility = React.useCallback((ratio: number) => {
    setLastSlideMostlyVisible(() => {
      // Disable next only when the last slide is at least 90% visible
      return ratio >= 0.9
    })
  }, [])
  const [layoutMeasured, setLayoutMeasured] = React.useState(false)

  const getSlides = React.useCallback((): HTMLElement[] => {
    const container = ref.current
    if (!container) {
      return []
    }
    return [...container.children].filter((child): child is HTMLElement =>
      (child as HTMLElement).classList?.contains('snap-start'),
    )
  }, [])

  const syncSlidesCount = React.useCallback(() => {
    const slides = getSlides()
    setSlidesCount(slides.length)
  }, [getSlides])

  // Keep slidesCount updated when children change
  React.useEffect(() => {
    if (!ref.current) {
      return
    }

    syncSlidesCount()

    const measureOverflow = () => {
      const container = ref.current
      if (!container) {
        setHasHorizontalOverflow(false)
        setLastSlideMostlyVisible(false)
        return
      }
      const overflow = container.scrollWidth > container.clientWidth + 1
      setHasHorizontalOverflow(overflow)
    }

    const measureLastVisibility = () => {
      const container = ref.current
      if (!container) {
        setLastSlideMostlyVisible(false)
        return
      }
      const slides = getSlides()
      if (slides.length === 0) {
        setLastSlideMostlyVisible(false)
        return
      }
      // oxlint-disable-next-line unicorn/prefer-at
      const last = slides[slides.length - 1]
      const containerRect = container.getBoundingClientRect()
      const lastRect = last.getBoundingClientRect()
      const left = Math.max(containerRect.left, lastRect.left)
      const right = Math.min(containerRect.right, lastRect.right)
      const visibleWidth = Math.max(0, right - left)
      const ratio = lastRect.width > 0 ? visibleWidth / lastRect.width : 0
      updateLastSlideVisibility(ratio)
    }

    // Initial measure
    measureOverflow()
    measureLastVisibility()
    setLayoutMeasured(getSlides().length > 0)

    mutationObserver.current = new MutationObserver(mutations => {
      let changed = false
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          changed = true
        }
      }
      if (changed) {
        syncSlidesCount()
        measureOverflow()
        measureLastVisibility()
        setLayoutMeasured(getSlides().length > 0)
        // If we were at the end and new slides appear, allow continuing
        // Do not auto-change index; next click will move forward
        // If index is out of bounds (e.g., children removed), clamp it
        setCurrentIndex(idx => {
          const maxIdx = Math.max(0, getSlides().length - 1)
          return Math.min(idx, maxIdx)
        })
      }
    })

    mutationObserver.current.observe(ref.current, { childList: true, subtree: false })

    // Observe container size changes to re-measure overflow
    const { current: containerEl } = ref
    const resizeObserver = new ResizeObserver(() => {
      measureOverflow()
      measureLastVisibility()
    })
    if (containerEl) {
      resizeObserver.observe(containerEl)
    }

    return () => {
      mutationObserver.current?.disconnect()
      mutationObserver.current = null
      resizeObserver.disconnect()
    }
  }, [slidesCount, syncSlidesCount, getSlides, updateLastSlideVisibility])

  // Optionally, update currentIndex on scroll to nearest slide
  React.useEffect(() => {
    const container = ref.current
    if (!container) {
      return
    }

    let rafId = 0
    const onScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        // Update last slide visibility on scroll
        const container = ref.current
        if (container) {
          const slides = getSlides()
          if (slides.length > 0) {
            // oxlint-disable-next-line unicorn/prefer-at
            const last = slides[slides.length - 1]
            const containerRect = container.getBoundingClientRect()
            const lastRect = last.getBoundingClientRect()
            const left = Math.max(containerRect.left, lastRect.left)
            const right = Math.min(containerRect.right, lastRect.right)
            const visibleWidth = Math.max(0, right - left)
            const ratio = lastRect.width > 0 ? visibleWidth / lastRect.width : 0
            updateLastSlideVisibility(ratio)
          }
        }
        const slides = getSlides()
        if (slides.length === 0) {
          return
        }
        const maxScrollLeft = (ref.current?.scrollWidth ?? 0) - (ref.current?.clientWidth ?? 0)
        // Use a small tolerance to avoid early atEnd detection on fractional pixels
        const atEnd = Math.abs((ref.current?.scrollLeft ?? 0) - maxScrollLeft) <= 2

        if (atEnd) {
          setCurrentIndex(slides.length - 1)
          return
        }

        const containerLeft = ref.current?.getBoundingClientRect().left ?? 0
        // Find the slide whose left edge is closest to container's left
        let bestIndex = 0
        let bestDelta = Infinity
        slides.forEach((slide, index) => {
          const delta = Math.abs(slide.getBoundingClientRect().left - containerLeft)
          if (delta < bestDelta) {
            bestDelta = delta
            bestIndex = index
          }
        })
        setCurrentIndex(bestIndex)
      })
    }

    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', onScroll)
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [getSlides, updateLastSlideVisibility])

  const getLeftmostVisibleIndex = React.useCallback((): number => {
    const container = ref.current
    if (!container) {
      return 0
    }
    const slides = getSlides()
    if (slides.length === 0) {
      return 0
    }
    const containerLeft = container.getBoundingClientRect().left

    // Prefer the first slide whose left edge is at or to the right of container's left
    let candidateIndex = 0
    let minNonNegativeDelta = Infinity

    for (let i = 0; i < slides.length; i++) {
      const delta = slides[i].getBoundingClientRect().left - containerLeft
      if (delta >= -1 && delta < minNonNegativeDelta) {
        minNonNegativeDelta = delta
        candidateIndex = i
      }
    }
    if (minNonNegativeDelta !== Infinity) {
      return candidateIndex
    }

    // Fallback: slide whose left edge is closest to container's left
    let bestIndex = 0
    let bestAbs = Infinity

    for (let i = 0; i < slides.length; i++) {
      const deltaAbs = Math.abs(slides[i].getBoundingClientRect().left - containerLeft)
      if (deltaAbs < bestAbs) {
        bestAbs = deltaAbs
        bestIndex = i
      }
    }
    return bestIndex
  }, [getSlides])

  const scrollToIndex = React.useCallback(
    (index: number) => {
      const container = ref.current
      if (!container) {
        return
      }
      const slides = getSlides()
      const node = slides[index]
      if (!node) {
        return
      }
      const rect = node.getBoundingClientRect()
      container.scrollBy({
        left: rect.left - container.getBoundingClientRect().left,
        behavior: 'smooth',
      })
    },
    [getSlides],
  )

  const onNextClick = React.useCallback(() => {
    setCurrentIndex(prev => {
      const next = Math.min(prev + 1, Math.max(0, getSlides().length - 1))
      if (next !== prev) {
        scrollToIndex(next)
      }
      return next
    })
  }, [getSlides, scrollToIndex])

  const onPreviousClick = React.useCallback(() => {
    const leftmostVisibleIndex = getLeftmostVisibleIndex()
    const targetIndex = Math.max(leftmostVisibleIndex - 1, 0)
    setCurrentIndex(prev => {
      if (targetIndex !== prev) {
        scrollToIndex(targetIndex)
      }
      return targetIndex
    })
  }, [getLeftmostVisibleIndex, scrollToIndex])

  const nextButtonProps = React.useMemo(
    () => ({
      disabled: layoutMeasured
        ? slidesCount === 0 ||
          !hasHorizontalOverflow ||
          currentIndex >= slidesCount - 1 ||
          lastSlideMostlyVisible
        : false,
      onClick: onNextClick,
    }),
    [
      layoutMeasured,
      slidesCount,
      hasHorizontalOverflow,
      lastSlideMostlyVisible,
      currentIndex,
      onNextClick,
    ],
  )

  const prevButtonProps = React.useMemo(
    () => ({
      disabled: currentIndex <= 0,
      onClick: onPreviousClick,
    }),
    [currentIndex, onPreviousClick],
  )

  return {
    containerRef: ref,
    nextButtonProps,
    prevButtonProps,
  }
}
